import { useCallback } from 'react';
import useSnackbarSystem from '@ui/systems/useSnackbarSystem';
import useReduxPopupSystem from 'src/new_arch/hooks/useReduxPopupSystem';
import Project from 'src/entities/project';

import ProjectMember from '../../models/projectMember';
import useDeleteProjectMemberMutation from '../../queries/useDeleteProjectMemberMutation';

type UseDeleteProjectMemberProps = {
	project: Project;
};

const useDeleteProjectMember = ( { project }: UseDeleteProjectMemberProps ) => {
	const snackbarSystem = useSnackbarSystem();
	const popupSystem = useReduxPopupSystem();

	const { mutate: deleteProjectMember } = useDeleteProjectMemberMutation( {
		projectID: project.id,
		onSuccess: member => (
			snackbarSystem.showSuccessMessage( {
				title: 'Member deleted',
				description: `${member.user.name} has been removed from the project`
			} )
		),
		onError: () => snackbarSystem.showErrorMessage( { title: 'Error deleting member' } )
	} );

	return useCallback( ( member: ProjectMember ) => {
		popupSystem.showDefaultConfirmationDialog( {
			title: 'Are you sure you want to delete this member?',
			description: `Are you sure you want to delete <strong>${member.user.name}</strong> from the project? This action cannot be undone.`,
			acceptText: 'Delete',
			cancelText: 'Cancel',
			descriptionAlignment: 'left',
			acceptButtonColor: 'red'
		} )
			.then( ( confirm: boolean ) => {
				popupSystem.hideConfirmationDialog();
				if ( confirm ) deleteProjectMember( member );
			} );
	}, [ deleteProjectMember ] );
};

export default useDeleteProjectMember;
